import React from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { FormattedMessage } from 'react-intl';
import { CLONED_CONNECTION, ConnectionVersion } from '../../../../../constants/common.constants';
import { ConnectionType, IConnectionDetailsDTO, IDeleteConnectionRequestParams, IKnovioLiveStreamDTO, KnovioLabel } from '../../../../../api/connections.dao';
import TrackingServiceProvider from '../../../../../tracking-service/tracking-service-provider';
import { IScheduledZoomMeetingDetailsDTO, IScheduledZoomWebinarDetailsDTO, IZoomDAO, PanelistLinkDeletionRequestParams } from '../../../../../api/zoom.dao';
import { PREFIX_HYPHEN_SPACE_REG_EXP, URL_WITH_PWD_REG_EXP } from '../../../../../constants/regex.constants';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { ILocalStorageService } from '../../../../../infrastructure/local-storage-service';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../../infrastructure/sub-windows';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { Space } from '../../../../../constants/html-character-entities';
import { alertServerError, showNotification } from '../../../../global/modal/alerts-host/alert-templates';
import { WebinarReportsViewModel } from '../webinar-report/webinar-reports.viewmodel';
import { IContainerProps } from './connections-list.presentation';
import { ConnectionForm } from './_connection-form/connection-form';
import { CreateZoomWebinarVM } from './_create-zoom-webinar-modal/create-zoom-webinar.viewmodel';
import { ZoomMeetingVM } from './_zoom-meeting-modal/zoom-meeting.viewmodel';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, OpenFrom, TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import { KnovioLiveStreamVariant, KnovioLiveStreamVM, KnovioLiveStreamVMProps } from './knovio-live-stream-modal/knovio-live-stream.viewmodel';
import { IDeleteConnectionDetails } from './_connection-delete-confirm-popover';
import { EventExecutionRuntime } from '../../../../../domain/jobs/implementations/event-execution-runtime';
import { CreateAudioBridgeVM } from './_audio-bridge-modal/create-audio-bridge.viewmodel';
import { IGlobalStorage } from '../../../../../infrastructure/global-storage';
import { getDateString } from '../../../../../infrastructure/utils/date.util';
import {
    alertMessage,
    audioBrdigeWarningMessage,
    cancelConnectionText,
    cancelCreateAudioBridgeMessage,
    deleteConnectionText,
    goBackText
} from '../../../../../constants/connections-labels.constants';
import { CreatePanelistLinkVM } from './panelist-link-modal/create-panelist-link.viewmodel';
import { IAttendeeOrganizationDTO } from '../../../../../api/attendees.dao';
import { autoSyncConnectionsLimitV1, autoSyncConnectionsLimitV2 } from '../../../../../modules/connections/const';
import { DateFnsFormats } from '../../../../../infrastructure/date-formatter';
import { cancelCreatePanelistLinkMessage, panelistDeletionToastMessage, panelistLinkWarningMessage } from '../../../../../constants/panelist-link-connection.constants';
import { trackMixPanelPanelistDeletion } from '../../../../../infrastructure/utils/connections.utils';

export interface IConnectionTypeInternal {
    id: string;
    name: string;
    disabled?: boolean;
}

class ConnectionsListVM {
    @mobx.observable
    private isFormDataReady: boolean;
    public zoomMeetingVM: ZoomMeetingVM;
    public createZoomWebinarVM: CreateZoomWebinarVM;
    private webinarReportsVM: WebinarReportsViewModel;
    public knovioLiveStreamVM: KnovioLiveStreamVM;
    public createAudioBridgeVM: CreateAudioBridgeVM;
    public createPanelistLinkVM: CreatePanelistLinkVM;
    public audioBridgeName: string;
    @mobx.observable
    private newConnectionSubWindow: ISubWindowFacade;
    @mobx.observable
    private readonly connectionIdToEditPopupMap: { [key: string]: ISubWindowFacade };
    @mobx.observable
    private isKnovioPreCheckLoading: boolean;

    private readonly _props: IContainerProps;

    constructor(
        props,
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        private readonly localeStorageService: ILocalStorageService = IoC.Container.get(ILocalStorageService),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly zoomDao: IZoomDAO = IoC.Container.get(IZoomDAO),
        @IoC.Inject private readonly eventExecutionRuntime = IoC.Container.get(EventExecutionRuntime),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
    ) {
        this.isKnovioPreCheckLoading = false;
        this._props = props;
        this.connectionIdToEditPopupMap = {};
        this.audioBridgeName = this.getAudioBridgeName();
        this.initZoomMeetingVM();
        this.initZoomWebinarVM();
        this.initWebinarReportsVM();
        this.initKnovioLiveStreamVM();
        this.initAudioBridgeVM();
        this.initPanelistLinkVM();
    }

    public get knovioPreCheckLoading(): boolean {
        return this.isKnovioPreCheckLoading;
    }

    public getProps = () => this._props;

    public get isAutoSyncLimitReached(): boolean {
        const autoSyncConnectionsCount = this.countAutoSyncConnections();
        return this._props.connectionVersion === ConnectionVersion.V2
            ? autoSyncConnectionsCount >= autoSyncConnectionsLimitV2
            : autoSyncConnectionsCount >= autoSyncConnectionsLimitV1;
    }

    private getConnectionById(meetingConnectionId: number): IConnectionDetailsDTO {
        return this._props.connections
            .find(connection => connection.meetingConnectionId === meetingConnectionId);
    }

    public getEventTimeZone = (): string => this._props.timeZone;

    public getAudioBridgeName(): string {
        const event = this.globalStorage.getEvent();
        const oeNumber = event?.oeNumber || '';
        const meetingTopic = this._props?.meetingTopic || event?.eventTitleForPassport || event?.name || '';
        const startDate = this._props?.startDate ? getDateString(this._props?.startDate, DateFnsFormats.StringifiedShortDayMonthYearDate) : '';
        const accountName = this._props?.organization ? this._props?.organization?.replace(PREFIX_HYPHEN_SPACE_REG_EXP, '') : '';

        const audioBridgeNameParts = [oeNumber, meetingTopic, startDate, accountName];
        const audioBridgeName = audioBridgeNameParts.filter(Boolean).join(' - ');
        return audioBridgeName;
    }

    public getWebinarConnections(): IConnectionDetailsDTO[] {
        return this._props.connections.filter(connection => connection.type === ConnectionType.Webinar);
    }

    public getPresenters(): IAttendeeOrganizationDTO[]{
        return this._props.organizationsForSpeakersSync?.presenters;
    }

    public getHosts(): IAttendeeOrganizationDTO[]{
        return this._props.organizationsForSpeakersSync?.hosts;
    }

    public getSupporters(): IAttendeeOrganizationDTO[]{
        return this._props.organizationsForSpeakersSync?.supports;
    }

    private countAutoSyncConnections(): number {
        return this._props.connections.filter(connection => connection.autoSyncEnabled).length;
    }

    private initZoomMeetingVM(): void {
        this.zoomMeetingVM = new ZoomMeetingVM(
            this._props.timeZone, this._props.topic, this._props.startDate,
            this._props.endDate, this.createZoomMeetingCallback,
            this._props.event.accountId,
            null, null,
        );
        this.zoomMeetingVM.setConnectionVersion(this._props.connectionVersion);
    }

    private initZoomWebinarVM(): void {
        this.createZoomWebinarVM = new CreateZoomWebinarVM(
            this._props.timeZone,
            this._props.topic,
            this._props.startDate,
            this._props.endDate,
            this.createZoomWebinarCallback,
            this.getProps().event
        );
        this.createZoomWebinarVM.setConnectionVersion(this._props.connectionVersion);
    }

    private initWebinarReportsVM(): void {
        this.webinarReportsVM = new WebinarReportsViewModel(this._props.event.accountId, this._props.event.eventId, this._props.openFrom);
    }

    private initKnovioLiveStreamVM(): void {
        const props: KnovioLiveStreamVMProps = {
            meetingTopic: this._props.meetingTopic,
            startDateTime: this._props.startDate,
            timeZone: this._props.timeZone,
            presenterOrganizationList: this._props.presenterOrganizationList,
            meetingApiParams: this._props.meetingApiParams,
        };

        this.knovioLiveStreamVM = new KnovioLiveStreamVM(props);
    }

    private initAudioBridgeVM(): void {
        this.createAudioBridgeVM = new CreateAudioBridgeVM(
            this._props.timeZone,
            this._props.startDate,
            this._props.endDate,
            this.audioBridgeName,
            this.createAudioBridgeConnectionCallback,
            this.getProps().event,
            this._props.isMeetingV2
        );
    }

    private initPanelistLinkVM(): void {
        this.createPanelistLinkVM = new CreatePanelistLinkVM(
            this.getWebinarConnections(),
            this.getPresenters(),
            this.getHosts(),
            this.getSupporters(),
            this.createPanelistLinkConnectionCallback,
            this.getProps().meetingApiParams,
            this._props?.connectionVersion,
        );
    }

    public getWebinarReportsVM(): WebinarReportsViewModel {
        return this.webinarReportsVM;
    }

    public get connectionTypes(): IConnectionTypeInternal[] {
        return this._props.connectionTypes;
    }

    public connectionTypesWithOption(): IConnectionTypeInternal[] {
        const connectionTypes = this.connectionTypes.map(connection => {
            if (connection?.id === ConnectionType.Knovio) {
                connection.name = KnovioLabel.OEStream;
            };
            return connection;
        });
        return connectionTypes;
    }

    public isWebinarConnectionExist(): boolean {
        return this._props.connections.some(connection => connection.type === ConnectionType.Webinar) && this._props.openFrom !== OpenFrom.Rooms;
    }

    public get isMeetingConnectionsForm(): boolean {
        return this._props.isMeetingConnectionsForm;
    }

    public deleteConnectionInternal = async (data: IDeleteConnectionRequestParams, connectionDetails: IDeleteConnectionDetails): Promise<void> => {
        const existingSubWindow = this.connectionIdToEditPopupMap[data.meetingConnectionId];
        if (existingSubWindow && existingSubWindow.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindow.close();
        }
        this.trackingService.get().track(TRACKING_EVENTS.DELETE_CONNECTION, {
            'Open From': OpenFrom.Connections
        });
        return await this._props.deleteConnection(data, connectionDetails);
    };

    public handleCloneConnection = (connection: IConnectionDetailsDTO): void => {
        const localeStorageData = {
            name: CLONED_CONNECTION,
            value: connection,
            creationDate: Date.now()
        };
        this.localeStorageService.setLocalStorageValue(localeStorageData);
        this.trackingService.get().track(CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.CLONE_CONNECTION, {
            'Open From': OpenFrom.Connections
        });
    };

    public handleGetCloneConnection = (): IConnectionDetailsDTO => {
        const data = this.localeStorageService.getLocalStorageValue<{
            creationDate: Date | string | number;
            value: IConnectionDetailsDTO;
        }>(CLONED_CONNECTION);
        // eslint-disable-next-line no-unused-vars
        const { meetingConnectionId, ...restValue } = data.value;
        return restValue;
    };

    public isCloneConnectionExist(): boolean {
        return this.localeStorageService.isLocalStorageValueExist(CLONED_CONNECTION);
    }

    public onEditConnection = (connection: IConnectionDetailsDTO): void => {
        const isEdit = this.zoomMeetingVM.getEditModeStatus();
        const currentConnection = this.getConnectionById(connection.meetingConnectionId);

        if (connection.type === ConnectionType.Zoom && !isEdit) {
            this.zoomMeetingVM.getZoomDetails({
                ...connection,
                autoRegistrationEnabled: currentConnection.autoRegistrationEnabled,
                autoSyncEnabled: currentConnection.autoSyncEnabled,
            });
            this.zoomMeetingVM.setUpdateZoomMeetingCallback(this.updateZoomMeetingCallback);
            return;
        }

        if (connection.type === ConnectionType.Webinar && !this.createZoomWebinarVM.isEditMode) {
            this.createZoomWebinarVM.openEditWebinarModal(connection);
            this.createZoomWebinarVM.setUpdateWebinarMeetingCallback(this.updateWebinarCallback);
            return;
        }

        if (connection.type === ConnectionType.Knovio && hasPermission(Permission.EDIT_KNOVIO_LIVE_STREAMS)) {
            this.initKnovioEditLiveStreamModal(connection);
            return;
        }

        this.openEditConnectionPopUp(connection);

    };

    public onConnectionTypeSelection = (connectionType: ConnectionType): void => {
        if (connectionType === ConnectionType.Zoom) {
            this.zoomMeetingVM.showForm();
        } else if (connectionType === ConnectionType.Webinar) {
            this.createZoomWebinarVM.showForm();
        } else if (connectionType === ConnectionType.Knovio && hasPermission(Permission.CREATE_KNOVIO_LIVE_STREAMS)) {
            this.knovioLiveStreamVM.setKnovioConnectionCallback(this.createKnovioConnectionCallback);
            this.knovioLiveStreamVM.setBehavior(KnovioLiveStreamVariant.CREATE);
            this.knovioLiveStreamVM.openModal();
        } else if (connectionType === ConnectionType.AudioBridge && hasPermission(Permission.CREATE_AUDIO_BRIDGE_CONNECTION)) {
            this.createAudioBridgeVM.showForm();
        } else if (connectionType === ConnectionType.PanelistLink) {
            this.createPanelistLinkVM.showForm();
        } else {
            this.openNewConnectionPopUp({ type: connectionType });
        }
    };

    public createZoomMeetingCallback = (
        zoomMeetingDetails?: IScheduledZoomMeetingDetailsDTO,
        showZoomAutoRegAndAutoSyncToggles?: boolean
    ): void => {
        let connectionDetails: IConnectionDetailsDTO = {
            type: ConnectionType.Zoom
        };
        if (zoomMeetingDetails)
            connectionDetails = {
                ...connectionDetails,
                connectionAccount: zoomMeetingDetails.zoomUser.email,
                connectionId: zoomMeetingDetails.zoomId,
                connectionLink: zoomMeetingDetails.joinUrl,
                alternativeLink: zoomMeetingDetails.joinUrl.replace(URL_WITH_PWD_REG_EXP, ''),
                hostLink: zoomMeetingDetails.startUrl,
                connectionPassword: zoomMeetingDetails.password,
                meetingInvitation: zoomMeetingDetails.invitation,
                sip: zoomMeetingDetails.sip,
                connectionTopic: zoomMeetingDetails.topic,
                connectionHostKey: zoomMeetingDetails.zoomUser?.hostKey,
                approvalType: zoomMeetingDetails?.approvalType ?? null,
                registrantsConfirmationEmail: zoomMeetingDetails?.registrantsConfirmationEmail ?? null,
                autoRegistrationEnabled: zoomMeetingDetails?.autoRegistrationEnabled ?? null,
                autoSyncEnabled: zoomMeetingDetails?.autoSyncEnabled ?? null,
            };
        this.openNewConnectionPopUp(connectionDetails, showZoomAutoRegAndAutoSyncToggles);
    };

    public updateZoomMeetingCallback = (zoomMeetingDetails?: IConnectionDetailsDTO): void => {
        this.onEditConnection(zoomMeetingDetails);
    };

    public updateWebinarCallback = (webinarConnectionDetails: IConnectionDetailsDTO): void => {
        this.onEditConnection(webinarConnectionDetails);
    };

    public openEditConnectionPopUp(connection: IConnectionDetailsDTO, knovioConnectionDetails?: IConnectionDetailsDTO): void {
        const existingSubWindow = this.connectionIdToEditPopupMap[connection.meetingConnectionId];
        const currentConnection = this.getConnectionById(connection.meetingConnectionId);

        let shallOpenNewSubWindow = true;
        if (existingSubWindow && existingSubWindow.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindow.maximize();
            shallOpenNewSubWindow = false;
        }
        if (shallOpenNewSubWindow) {
            this.connectionIdToEditPopupMap[connection.meetingConnectionId] = this.subWindowService.showSubWindow({
                title: this._props.intl.formatMessage({
                    id: 'oe.connection.editDetails',
                    description: 'Edit Popover header text',
                    defaultMessage: 'Edit Connection Details'
                }),
                componentToHost: ConnectionForm,
                componentToHostProps: {
                    meetingConnectionDetails: {...connection, blueprintId: currentConnection?.blueprintId },
                    meetingApiParams: this._props.meetingApiParams,
                    executeSubmitApiCall: async (connectionDetails: IConnectionDetailsDTO): Promise<void> => {
                        return await this._props.updateConnection(connectionDetails);
                    },
                    deleteConnection: async (data: IDeleteConnectionRequestParams, connectionDetails: IDeleteConnectionDetails) => {
                        await this.deleteConnectionInternal(data, connectionDetails);
                    },
                    getKnovioConnectionDetails: async (connectionDetails: IConnectionDetailsDTO): Promise<IConnectionDetailsDTO> => {
                        return knovioConnectionDetails
                            ? knovioConnectionDetails
                            : await this._props.getKnovioConnectionDetails(connectionDetails);
                    },
                    shouldShowKnovioReplayToggle: this._props.isKnovioReplayConnectionExist
                        ? this._props.isKnovioReplayConnectionExist(connection)
                        : false
                    ,
                    organizationsForSpeakersSync: this._props.organizationsForSpeakersSync,
                    connectionVersion: this._props.connectionVersion
                }
            });
        }
    }

    public openNewConnectionPopUp(meetingConnectionDetails: IConnectionDetailsDTO, showZoomAutoRegAndAutoSyncToggles?: boolean): void {
        const isClonedConnecting = meetingConnectionDetails.type.toString() === CLONED_CONNECTION;
        const isZoomMeeting = meetingConnectionDetails.connectionId &&
            meetingConnectionDetails.type === ConnectionType.Zoom;
        const isWebinar = meetingConnectionDetails.connectionId &&
            meetingConnectionDetails.type === ConnectionType.Webinar;
        const isAudioBridge = meetingConnectionDetails.connectionId &&
            meetingConnectionDetails.type === ConnectionType.AudioBridge;
        const isPanelistLink = meetingConnectionDetails.connectionId &&
            meetingConnectionDetails.attendeeEmail &&
            meetingConnectionDetails.type === ConnectionType.PanelistLink;
        const handler = {
            [ConnectionType.Zoom]: () => this.onCloseZoomConnection(meetingConnectionDetails.connectionId),
            [ConnectionType.Webinar]: () => this.onCloseWebinarConnection(meetingConnectionDetails.connectionId),
            [ConnectionType.AudioBridge]: () => this.onCloseAudioBridgeConnection(meetingConnectionDetails.connectionId),
            [ConnectionType.PanelistLink]: () => this.onClosePanelistLinkConnection({
                accountId: this._props.event.accountId,
                eventId: this._props.event.eventId,
                webinarId: meetingConnectionDetails.connectionId,
                panelistEmail: meetingConnectionDetails.attendeeEmail,
            })
        };
        const isDraftConnection = meetingConnectionDetails.meetingConnectionId;
        this.newConnectionSubWindow = this.subWindowService.showSubWindow({
            title: this._props.intl.formatMessage({
                id: 'oe.connection.details',
                description: 'Popover header text',
                defaultMessage: 'New Connection'
            }),
            componentToHost: ConnectionForm,
            componentToHostProps: {
                isMeetingConnectionsForm: this._props.isMeetingConnectionsForm,
                showZoomAutoRegAndAutoSyncToggles: showZoomAutoRegAndAutoSyncToggles,
                meetingConnectionDetails: isClonedConnecting
                    ? this.handleGetCloneConnection()
                    : meetingConnectionDetails,
                meetingApiParams: this._props.meetingApiParams,
                executeSubmitApiCall: async (connectionDetails: IConnectionDetailsDTO): Promise<void> => {
                    return isDraftConnection
                        ? await this._props.updateConnection(connectionDetails)
                        : await this._props.createConnection(connectionDetails);
                },
                onCloseZoomConnection: handler[meetingConnectionDetails.type],
                isClonedConnection: isClonedConnecting,
                shouldShowKnovioReplayToggle: this._props.isKnovioReplayConnectionExist
                    ? this._props.isKnovioReplayConnectionExist(meetingConnectionDetails)
                    : false
                ,
                zoomMeetingType: this.zoomMeetingVM.zoomMeetingType,
                organizationsForSpeakersSync: this._props.organizationsForSpeakersSync,
                connectionVersion: this._props.connectionVersion
            },
            onCloseIconClickHandler: isZoomMeeting || isWebinar || isAudioBridge || isPanelistLink ? handler[meetingConnectionDetails.type] : null,
        });
    }

    public createZoomWebinarCallback = (zoomWebinarDetails?: IScheduledZoomWebinarDetailsDTO, showZoomAutoRegAndAutoSyncToggles?: boolean) => {
        let connectionDetails: IConnectionDetailsDTO = {
            type: ConnectionType.Webinar
        };
        if (zoomWebinarDetails) {
            connectionDetails = {
                ...connectionDetails,
                zoomDescription: zoomWebinarDetails.agenda,
                connectionAccount: zoomWebinarDetails.zoomUser.email,
                connectionId: zoomWebinarDetails.zoomId,
                connectionLink: zoomWebinarDetails.joinUrl,
                alternativeLink: zoomWebinarDetails.joinUrl.replace(URL_WITH_PWD_REG_EXP, ''),
                hostLink: zoomWebinarDetails.startUrl,
                connectionPassword: zoomWebinarDetails.password,
                meetingInvitation: zoomWebinarDetails.invitation,
                sip: zoomWebinarDetails.sip,
                connectionTopic: zoomWebinarDetails.topic,
                connectionHostKey: zoomWebinarDetails.zoomUser.hostKey,
                autoRegistrationEnabled: zoomWebinarDetails?.autoRegistrationEnabled ?? false,
                autoSyncEnabled: zoomWebinarDetails?.autoSyncEnabled ?? false,
                approvalType: zoomWebinarDetails?.approvalType ?? null,
                registrantsConfirmationEmail: zoomWebinarDetails?.registrantsConfirmationEmail ?? null,
                registrationUrl: zoomWebinarDetails.registrationUrl || '',
                autoRecording: zoomWebinarDetails.autoRecording
            };
        }
        this.openNewConnectionPopUp(connectionDetails, showZoomAutoRegAndAutoSyncToggles);
    };

    public editKnovioConnectionCallback = (connectionDetailsDTO: IConnectionDetailsDTO, knovioConnectionDetails?: IConnectionDetailsDTO): void => {
        this.openEditConnectionPopUp(connectionDetailsDTO, knovioConnectionDetails);
    };

    public createKnovioConnectionCallback = (connectionDetailsDTO: IConnectionDetailsDTO): void => {
        this.openNewConnectionPopUp(connectionDetailsDTO);
    };

    public createAudioBridgeConnectionCallback = (connectionDetailsDTO: IConnectionDetailsDTO): void => {
        let connectionDetails: IConnectionDetailsDTO = {
            type: ConnectionType.AudioBridge
        };
        if (connectionDetailsDTO) {
            connectionDetails = {
                ...connectionDetailsDTO,
                connectionId: connectionDetailsDTO.audioBridgeID,
            };
        }
        this.openNewConnectionPopUp(connectionDetails);
    };

    public createPanelistLinkConnectionCallback = (connectionDetailsDTO: IConnectionDetailsDTO): void => {
        this.openNewConnectionPopUp(connectionDetailsDTO);
    };

    public closeSubWindow(): boolean {
        if (this.newConnectionSubWindow) this.newConnectionSubWindow.close();
        return true;
    }

    public onCloseWebinarConnection = (zoomMeetingId: string): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.common.confirmMessageStart'
                        defaultMessage='Are you sure you want to '
                        description='Label text'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.meeting.cancelCreatingZoomWebinarMeeting'
                            defaultMessage='cancel creating this Zoom Webinar connection'
                            description='Label text'
                        />
                    </span>
                    ?
                    <br />
                    <span className='alert-message'>
                        <FormattedMessage
                            id='oe.meeting.permanentlyDeleteBothOrCancelOnlyTheConnection'
                            description='Warning message'
                            defaultMessage='You can choose to permanently delete both the Zoom Webinar and the Connection, or to cancel only the Connection.'
                        />
                    </span>
                </span>
            ),
            confirmButtonProps: {
                text: <FormattedMessage
                    id='oe.common.alerts.yesCancelOnlyConnection'
                    description='Button label'
                    defaultMessage='Yes, Cancel only Connection'
                />,
                modifier: 'confirm-warn',
                handler: () => {
                    this.closeSubWindow();
                },
            },
            extendConfirmButtonProps: {
                text: <FormattedMessage
                    id='oe.common.alerts.yesDeleteBoth'
                    description='Button label'
                    defaultMessage='Yes, Delete Both'
                />,
                modifier: 'confirm-warn',
                handler: () => {
                    this.deleteCurrentWebinarMeeting(zoomMeetingId);
                },
            },
            cancelButtonProps: {
                text: <FormattedMessage
                    id='oe.common.alerts.goBack'
                    description='Button label'
                    defaultMessage='Go Back'
                />,
                modifier: 'cancel'
            }
        });
    };

    public onCloseZoomConnection = (zoomMeetingId: string): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.common.confirmMessageStart'
                        defaultMessage='Are you sure you want to '
                        description='Label text'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.meeting.cancelCreatingZoomMeeting'
                            defaultMessage='cancel creating this Zoom connection'
                            description='Label text'
                        />
                    </span>
                    ?
                    <br />
                    <span className='alert-message'>
                        <FormattedMessage
                            id='oe.meeting.permanentlyDeleteBothOrCancelOnlyTheConnection'
                            description='Warning message'
                            defaultMessage='You can choose to permanently delete both the Zoom Meeting and the Connection, or to cancel only the Connection.'
                        />
                    </span>
                </span>
            ),
            confirmButtonProps: {
                text: <FormattedMessage
                    id='oe.common.alerts.yesCancelOnlyConnection'
                    description='Button label'
                    defaultMessage='Yes, Cancel only Connection'
                />,
                modifier: 'confirm-warn',
                handler: () => {
                    this.closeSubWindow();
                },
            },
            extendConfirmButtonProps: {
                text: <FormattedMessage
                    id='oe.common.alerts.yesDeleteBoth'
                    description='Button label'
                    defaultMessage='Yes, Delete Both'
                />,
                modifier: 'confirm-warn',
                handler: () => {
                    this.deleteCurrentMeeting(zoomMeetingId);
                },
            },
            cancelButtonProps: {
                text: <FormattedMessage
                    id='oe.common.alerts.goBack'
                    description='Button label'
                    defaultMessage='Go Back'
                />,
                modifier: 'cancel'
            }
        });
    };

    public onCloseAudioBridgeConnection = (audioBrdigeMeetingId: string): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    {alertMessage}
                    <Space />
                    <span className='alert-message--danger'>{cancelCreateAudioBridgeMessage}</span>
                    ?
                    <br />
                    <span className='alert-message'>{audioBrdigeWarningMessage}</span>
                </span>
            ),
            confirmButtonProps: {
                text: cancelConnectionText,
                modifier: 'confirm-warn',
                handler: () => {
                    this.closeSubWindow();
                },
            },
            extendConfirmButtonProps: {
                text: deleteConnectionText,
                modifier: 'confirm-warn',
                handler: () => {
                    this.deleteCurrentAudioBridge(audioBrdigeMeetingId);
                },
            },
            cancelButtonProps: {
                text: goBackText,
                modifier: 'cancel'
            }
        });
    };

    public onClosePanelistLinkConnection = (panelistLinkDetails: PanelistLinkDeletionRequestParams): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    {alertMessage}
                    <Space />
                    <span className='alert-message--danger'>{cancelCreatePanelistLinkMessage}</span>
                    ?
                    <br />
                    <span className='alert-message'>{panelistLinkWarningMessage}</span>
                </span>
            ),
            confirmButtonProps: {
                text: cancelConnectionText,
                modifier: 'confirm-warn',
                handler: () => {
                    this.closeSubWindow();
                },
            },
            extendConfirmButtonProps: {
                text: deleteConnectionText,
                modifier: 'confirm-warn',
                handler: () => {
                    this.deleteCurrentPanelistLink(panelistLinkDetails);
                },
            },
            cancelButtonProps: {
                text: goBackText,
                modifier: 'cancel'
            }
        });
    };

    private initKnovioEditLiveStreamModal(connectionDetailsDTO: IConnectionDetailsDTO): void {
        this.setKnovioPreCheckLoadingState(true);
        this._props.getKnovioConnectionDetails(connectionDetailsDTO).then(response => {
            if (response.knovioEditMetadata) {
                const afterUpdateCallback = (dto: IKnovioLiveStreamDTO) => {
                    this.editKnovioConnectionCallback({ ...connectionDetailsDTO, knovioName: dto.title }, response);
                };
                this.knovioLiveStreamVM.setKnovioConnectionCallback(this.editKnovioConnectionCallback.bind(this, connectionDetailsDTO, response));
                this.knovioLiveStreamVM.setKnovioAfterUpdateCallback(afterUpdateCallback);
                this.knovioLiveStreamVM.setKnovioMetadata(response.knovioEditMetadata);
                this.knovioLiveStreamVM.setMeetingConnectionId(connectionDetailsDTO.meetingConnectionId);
                this.knovioLiveStreamVM.setKnovioPresentationId(response.knovioPresentationId);
                this.setKnovioPreCheckLoadingState(false);
                this.knovioLiveStreamVM.setBehavior(KnovioLiveStreamVariant.EDIT);
                this.knovioLiveStreamVM.openModal();
            } else {
                this.openEditConnectionPopUp(connectionDetailsDTO, response);
            }
        })
        .catch(() => {
            this.openEditConnectionPopUp(connectionDetailsDTO);
        })
        .finally(() => {
            this.setKnovioPreCheckLoadingState(false);
        });
    }

    @mobx.action
    private deleteCurrentMeeting = async (zoomMeetingId: string): Promise<void> => {
        try {
            await this.zoomDao.deleteScheduledMeeting(zoomMeetingId);
            this.closeSubWindow();
        } catch (e) {
            const errorMessage = 'There was an error deleting the Zoom Meeting. Please try again.';
            mobx.runInAction(() => {
                alertServerError('Error', this.errorParsingService.extractMessage(errorMessage));
            });
        }
    };

    @mobx.action
    private deleteCurrentWebinarMeeting = async (webinarId: string): Promise<void> => {
        try {
            await this.zoomDao.deleteWebinardMeeting({
                webinarId: webinarId,
                ...this._props.event
            });
            this.closeSubWindow();
        } catch (e) {
            const errorMessage = 'There was an error deleting the Webinar Meeting. Please try again.';
            mobx.runInAction(() => {
                alertServerError('Error', this.errorParsingService.extractMessage(errorMessage));
            });
        }
    };

    @mobx.action
    private deleteCurrentAudioBridge = async (audioBridgeId: string): Promise<void> => {
        try {
            await this.zoomDao.deleteScheduledAudioBridge({audioBridgeId: audioBridgeId});
            this.closeSubWindow();
        } catch (e) {
            const errorMessage = 'There was an error deleting the Audio Bridge. Please try again.';
            mobx.runInAction(() => {
                alertServerError('Error', this.errorParsingService.extractMessage(errorMessage));
            });
        }
    };

    @mobx.action
    private deleteCurrentPanelistLink = async (panelistLinkDetails: PanelistLinkDeletionRequestParams): Promise<void> => {
        try {
            await this.zoomDao.deleteScheduledPanelistLink(panelistLinkDetails);
            showNotification(panelistDeletionToastMessage);
            this.closeSubWindow();
        } catch (e) {
            const errorMessage = 'Panelist removal failed.';
            mobx.runInAction(() => {
                alertServerError('Error', this.errorParsingService.extractMessage(errorMessage));
            });
        }
        trackMixPanelPanelistDeletion(TRACKING_EVENTS.DELETE_PANELIST_LINK, this._props?.meetingApiParams.meetingId);
    };

    @mobx.action
    private setKnovioPreCheckLoadingState = (state: boolean): void => {
        this.isKnovioPreCheckLoading = state;
    };

    public trackEventMixPanel = (eventName: string): void => {
        this.trackingService.get().track(eventName,{
            [eventName]: 'Yes'
        });
    };

    public shouldDisableDeleteButton = (connection: IConnectionDetailsDTO): boolean => {
        return connection?.autoSyncEnabled && !this.eventExecutionRuntime.isReady();
    };
}

export { ConnectionsListVM };
